import React from "react"
import { Copy, GitMerge, Phone, Mail, CheckCircle2 } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Badge } from "src/components/ui/badge"
import { Skeleton } from "src/components/ui/skeleton"
import { LeadSourceBadge } from "./LeadSourceBadge"
import { LeadStatusBadge } from "./LeadStatusBadge"

// Shared light-surface shell, matching the leads table / scope tabs:
//   rounded-xl · border-slate-200 · bg-white · shadow-sm
const SURFACE = "rounded-xl border border-slate-200 bg-white shadow-sm"

function SkeletonCluster() {
  return (
    <div className={`${SURFACE} p-4 space-y-3`}>
      <Skeleton className="h-4 w-48 rounded" />
      <Skeleton className="h-16 w-full rounded" />
    </div>
  )
}

// ── Empty state ───────────────────────────────────────────────────────────────
function NoDuplicates() {
  return (
    <div className={`${SURFACE} flex flex-col items-center justify-center py-16 text-center`}>
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
        <CheckCircle2 className="h-6 w-6 text-emerald-500" />
      </div>
      <p className="text-sm font-medium text-slate-700">No duplicates found</p>
      <p className="mt-1 text-xs text-slate-400">
        Every buyer in this organization maps to a single lead.
      </p>
    </div>
  )
}

// ── One cluster card ──────────────────────────────────────────────────────────
function ClusterCard({ cluster, onMerge }) {
  const { key, size, sources, leads } = cluster
  const isPhone = !key?.includes("@")

  return (
    <div className={SURFACE}>
      <div className="flex items-start justify-between gap-3 p-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
            {isPhone ? (
              <Phone className="h-4 w-4 text-slate-400" />
            ) : (
              <Mail className="h-4 w-4 text-slate-400" />
            )}
            <span className="truncate">{key}</span>
            <Badge variant="secondary" className="font-normal text-slate-500">
              {size} leads
            </Badge>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-slate-400">across</span>
            {sources.map((s) => (
              <LeadSourceBadge key={s} source={s} />
            ))}
          </div>
        </div>
        <Button size="sm" className="gap-2 shrink-0" onClick={() => onMerge(cluster)}>
          <GitMerge className="h-4 w-4" />
          Review &amp; merge
        </Button>
      </div>

      {/* Member leads */}
      <div className="divide-y divide-slate-100 border-t border-slate-100">
        {leads.map((lead) => (
          <div key={lead.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
            <div className="min-w-0 truncate">
              <span className="font-medium text-slate-800">{lead.name}</span>
              <span className="ml-2 text-xs text-slate-400">
                {lead.assigned_to?.full_name ? `· ${lead.assigned_to.full_name}` : "· unassigned"}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <LeadStatusBadge status={lead.status} />
              <LeadSourceBadge source={lead.source} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Grouped duplicate view ────────────────────────────────────────────────────
// Replaces the flat leads table when the "Duplicate" scope tab is active.
export function DuplicateClusters({ clusters = [], loading, onMerge }) {
  if (loading) {
    return (
      <div className="space-y-4">
        {[0, 1, 2].map((i) => <SkeletonCluster key={i} />)}
      </div>
    )
  }

  if (clusters.length === 0) return <NoDuplicates />

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Copy className="h-3.5 w-3.5 text-slate-400" />
        {clusters.length} duplicate {clusters.length === 1 ? "group" : "groups"} — same buyer captured via multiple channels.
      </div>
      {clusters.map((cluster) => (
        <ClusterCard key={cluster.key} cluster={cluster} onMerge={onMerge} />
      ))}
    </div>
  )
}
